<template>
  <div class="admin-page">
    <v-row>
      <v-col cols="12" lg="4">
        <v-card class="admin-card" elevation="1">
          <v-card-title class="admin-card-title">
            <v-icon class="mr-2" size="20">mdi-pill</v-icon>
            {{
              editing && editingItem
                ? `Editing "${editingItem.name}"`
                : "Create Method"
            }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4">
            <v-form ref="form" v-model="valid" lazy-validation>
              <p class="admin-section-label">Basic info</p>

              <v-text-field
                v-model="name"
                variant="outlined"
                density="comfortable"
                :counter="40"
                :rules="[
                  (v) => !!v || 'Name is required',
                  (v) =>
                    (v && v.length <= 40) ||
                    'Name must be less than 40 characters',
                ]"
                label="Name"
                required
                class="mb-4 admin-field"
              ></v-text-field>

              <p class="admin-section-label">Method type</p>
              <div class="method-chip-picker mb-4">
                <button
                  v-for="type in methodTypes"
                  :key="type.name"
                  type="button"
                  class="method-chip"
                  :class="{ active: selectedMethodType === type.name }"
                  @click="selectedMethodType = type.name"
                >
                  {{ type.displayName }}
                </button>
              </div>

              <p class="admin-section-label">
                Landing page accordion
                <span class="admin-section-label-hint"
                  >— shown when a visitor expands this method on the landing
                  page</span
                >
              </p>

              <v-text-field
                v-model="tagline"
                variant="outlined"
                density="comfortable"
                label="Tagline"
                hint="Short effectiveness summary, e.g. 'More than 90% effective at preventing HIV.'"
                persistent-hint
                class="mb-4 admin-field"
              ></v-text-field>

              <v-text-field
                v-model="schedule"
                variant="outlined"
                density="comfortable"
                label="Schedule"
                hint="e.g. 'One pill every day for as long as you need it'"
                persistent-hint
                class="mb-4 admin-field"
              ></v-text-field>

              <div class="admin-subsection">
                <p class="admin-subsection-label">Process</p>
                <v-text-field
                  v-model="newProcessTime"
                  variant="outlined"
                  density="compact"
                  label="Time (e.g. 'Day 1:')"
                  hide-details
                  class="admin-field mb-2"
                ></v-text-field>
                <v-text-field
                  v-model="newProcessDetail"
                  variant="outlined"
                  density="compact"
                  label="Detail"
                  hide-details
                  class="admin-field mb-2"
                ></v-text-field>
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="newProcessPosition"
                    variant="outlined"
                    density="compact"
                    label="Position"
                    style="max-width: 100px"
                    hide-details
                    class="admin-field"
                  ></v-text-field>
                  <button
                    type="button"
                    class="pill-btn pill-btn-add ml-2"
                    @click="addProcessStep"
                  >
                    <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                    Add
                  </button>
                </div>

                <div class="admin-option-list">
                  <div
                    v-for="(step, index) in sortedProcess"
                    :key="index"
                    class="option-card"
                  >
                    <div class="option-card-top">
                      <span class="option-card-label"
                        >{{ step.position }}. {{ step.time }}
                        {{ step.detail }}</span
                      >
                      <v-icon
                        icon="mdi-delete"
                        size="18"
                        class="admin-option-delete"
                        @click="deleteProcessStep(step)"
                      ></v-icon>
                    </div>
                  </div>
                  <p v-if="process.length === 0" class="admin-option-empty">
                    No process steps added yet.
                  </p>
                </div>
              </div>

              <v-text-field
                v-model="note"
                variant="outlined"
                density="comfortable"
                label="Note"
                hint="Short reminder shown below the process, e.g. 'Works best if taken every day.'"
                persistent-hint
                class="mb-4 admin-field"
              ></v-text-field>

              <v-textarea
                v-model="resultText"
                variant="outlined"
                density="comfortable"
                :rules="[(v) => !!v || 'Result text is required']"
                label="Result text"
                required
                class="mb-2 admin-field"
              ></v-textarea>

              <p class="admin-section-label">Detail page content</p>

              <v-text-field
                v-model="whatIsIt"
                variant="outlined"
                density="comfortable"
                :rules="[(v) => !!v || 'What is it is required']"
                label="What is it?"
                required
                class="mb-2 admin-field"
              ></v-text-field>

              <v-text-field
                v-model="howDoesItWork"
                variant="outlined"
                density="comfortable"
                :rules="[(v) => !!v || 'How does it work is required']"
                label="How does it work?"
                required
                class="mb-2 admin-field"
              ></v-text-field>

              <v-text-field
                v-model="howIsItUsed"
                variant="outlined"
                density="comfortable"
                :rules="[(v) => !!v || 'How is it used is required']"
                label="How is it used?"
                required
                class="mb-2 admin-field"
              ></v-text-field>

              <v-text-field
                v-model="howWellDoesItWork"
                variant="outlined"
                density="comfortable"
                :rules="[(v) => !!v || 'How well does it work is required']"
                label="How well does it work?"
                required
                class="mb-2 admin-field"
              ></v-text-field>

              <v-text-field
                v-model="whatAreTheSideEffect"
                variant="outlined"
                density="comfortable"
                :rules="[(v) => !!v || 'What are the side effects is required']"
                label="What are the side effects?"
                required
                class="mb-4 admin-field"
              ></v-text-field>

              <div class="admin-subsection">
                <p class="admin-subsection-label">Considerations</p>
                <v-text-field
                  v-model="newConsideration"
                  variant="outlined"
                  density="compact"
                  label="Consideration"
                  hide-details
                  class="admin-field mb-2"
                ></v-text-field>
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="newConsiderationPosition"
                    variant="outlined"
                    density="compact"
                    label="Position"
                    style="max-width: 100px"
                    hide-details
                    class="admin-field"
                  ></v-text-field>
                  <button
                    type="button"
                    class="pill-btn pill-btn-add ml-2"
                    @click="addConsideration"
                  >
                    <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                    Add
                  </button>
                </div>

                <div class="admin-option-list">
                  <div
                    v-for="(consideration, index) in considerations"
                    :key="index"
                    class="option-card"
                  >
                    <div class="option-card-top">
                      <span class="option-card-label"
                        >{{ consideration.position }}.
                        {{ consideration.text }}</span
                      >
                      <v-icon
                        icon="mdi-delete"
                        size="18"
                        class="admin-option-delete"
                        @click="deleteConsideration(index)"
                      ></v-icon>
                    </div>
                  </div>
                  <p
                    v-if="considerations.length === 0"
                    class="admin-option-empty"
                  >
                    No considerations added yet.
                  </p>
                </div>
              </div>

              <div class="admin-subsection">
                <p class="admin-subsection-label">FAQs</p>
                <v-textarea
                  v-model="newFaqQuestion"
                  variant="outlined"
                  density="compact"
                  label="FAQ Question"
                  rows="2"
                  hide-details
                  class="admin-field mb-2"
                ></v-textarea>
                <v-textarea
                  v-model="newFaqAnswer"
                  variant="outlined"
                  density="compact"
                  label="FAQ Answer"
                  rows="2"
                  hide-details
                  class="admin-field mb-2"
                ></v-textarea>
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="newFaqPosition"
                    variant="outlined"
                    density="compact"
                    label="Position"
                    style="max-width: 100px"
                    hide-details
                    class="admin-field"
                  ></v-text-field>
                  <button
                    type="button"
                    class="pill-btn pill-btn-add ml-2"
                    @click="addFaq"
                  >
                    <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                    Add
                  </button>
                </div>

                <div class="admin-option-list">
                  <div
                    v-for="(faq, index) in faqs"
                    :key="index"
                    class="option-card"
                  >
                    <div class="option-card-top">
                      <span class="option-card-label"
                        >{{ faq.position }}. {{ faq.question }}</span
                      >
                      <v-icon
                        icon="mdi-delete"
                        size="18"
                        class="admin-option-delete"
                        @click="deleteFaq(index)"
                      ></v-icon>
                    </div>
                    <p class="option-card-answer">{{ faq.answer }}</p>
                  </div>
                  <p v-if="faqs.length === 0" class="admin-option-empty">
                    No FAQs added yet.
                  </p>
                </div>
              </div>

              <p class="admin-section-label">Image & position</p>

              <div class="admin-subsection admin-subsection-row">
                <v-file-input
                  accept="image/*"
                  label="Image"
                  variant="outlined"
                  density="comfortable"
                  show-size
                  hide-details
                  @change="onFilePicked"
                  v-model="imagePicker"
                  required
                  :rules="[(v) => !!v || 'An image is required']"
                  class="admin-field"
                ></v-file-input>
                <v-img
                  v-if="imageUrl"
                  class="ml-3 admin-image-preview"
                  :src="imageUrl"
                  :aspect-ratio="1"
                  height="56"
                  width="56"
                  fit
                />
              </div>

              <v-text-field
                v-model.number="position"
                variant="outlined"
                density="comfortable"
                :rules="[
                  (v) =>
                    Number.isInteger(Number(v)) ||
                    'The value must be an integer number',
                ]"
                label="Position"
                required
                class="mb-2 mt-4 admin-field"
              ></v-text-field>

              <p class="admin-section-label">Color</p>
              <v-color-picker
                v-model="color"
                class="mb-4 admin-color-picker"
                elevation="0"
              ></v-color-picker>

              <p class="admin-section-label">Visibility & eligibility</p>

              <v-switch
                v-model="removeFromDetailsPage"
                color="#3d80e8"
                :label="`Remove Method from Details Page`"
                hide-details
                class="mb-4"
              ></v-switch>

              <p class="admin-section-label">
                Applicable countries
                <span
                  v-if="applicableCountries.length === 0"
                  class="admin-section-label-error"
                  >— select at least one</span
                >
              </p>
              <div class="method-chip-picker mb-4">
                <button
                  v-for="country in countries"
                  :key="country"
                  type="button"
                  class="method-chip"
                  :class="{ active: applicableCountries.includes(country) }"
                  @click="toggleValue(applicableCountries, country)"
                >
                  {{ country }}
                </button>
              </div>

              <p class="admin-section-label">
                Negating questions
                <span class="admin-section-label-hint"
                  >— answers that disqualify this method entirely</span
                >
              </p>
              <div class="method-chip-picker mb-4">
                <button
                  v-for="question in allQuestions"
                  :key="question.id"
                  type="button"
                  class="method-chip method-chip-small"
                  :class="{ active: negatingQuestions.includes(question.id) }"
                  @click="toggleValue(negatingQuestions, question.id)"
                >
                  {{ question.text }}
                </button>
                <p v-if="allQuestions.length === 0" class="admin-option-empty">
                  No questions available yet.
                </p>
              </div>

              <div class="admin-actions">
                <button
                  type="button"
                  class="pill-btn pill-btn-outline"
                  @click="validate"
                >
                  <v-icon size="16" class="mr-1"
                    >mdi-check-circle-outline</v-icon
                  >
                  Validate
                </button>

                <button
                  type="button"
                  class="pill-btn pill-btn-outline pill-btn-danger"
                  @click="reset"
                >
                  <v-icon size="16" class="mr-1">mdi-restore</v-icon>
                  Reset
                </button>

                <button
                  type="button"
                  class="pill-btn pill-btn-solid"
                  @click="save"
                >
                  <v-icon size="16" class="mr-1"
                    >mdi-content-save-outline</v-icon
                  >
                  Save
                </button>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="8">
        <v-card class="admin-card" elevation="1">
          <v-card-title class="admin-card-title">
            <v-icon class="mr-2" size="20">mdi-view-carousel-outline</v-icon>
            Select a Method
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4">
            <div v-if="allMethods.length === 0" class="admin-option-empty">
              No methods yet — create one on the left.
            </div>

            <div v-else class="method-tab-bar">
              <button
                v-for="item in allMethods"
                :key="item.id"
                type="button"
                class="method-tab"
                :class="{ active: editingItem && editingItem.id === item.id }"
                @click="editItem(item)"
              >
                <span class="method-tab-icon-wrap">
                  <img
                    v-if="item.imageUrl"
                    :src="item.imageUrl"
                    :alt="item.name"
                    class="method-tab-icon"
                  />
                </span>
                <span class="method-tab-label">{{ item.name }}</span>
              </button>
            </div>

            <div
              v-if="editingItem && editingItem.id"
              class="method-tab-selected-actions"
            >
              <span class="method-tab-selected-label"
                >Editing "{{ editingItem.name }}"</span
              >
              <div class="d-flex" style="gap: 8px">
                <button
                  type="button"
                  class="pill-btn pill-btn-outline pill-btn-small"
                  @click="cloneItem(editingItem)"
                >
                  <v-icon size="14" class="mr-1">mdi-content-copy</v-icon>
                  Duplicate
                </button>
                <button
                  type="button"
                  class="pill-btn pill-btn-outline pill-btn-danger pill-btn-small"
                  @click="deleteItem(editingItem)"
                >
                  <v-icon size="14" class="mr-1">mdi-delete</v-icon>
                  Delete
                </button>
              </div>
            </div>

            <button
              type="button"
              class="pill-btn pill-btn-outline mt-4"
              @click="cleanNegatingQuestions()"
            >
              <v-icon size="16" class="mr-1">mdi-broom</v-icon>
              Clean negating questions after removing a question
            </button>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import MethodDataService from "../services/MethodDataService";
import QuestionDataService from "../services/QuestionDataService";

export default {
  name: "MethodAdmin",
  data() {
    return {
      valid: true,
      editing: false,
      editingItem: "",
      allMethods: [],
      name: "",
      tagline: "",
      schedule: "",
      process: [],
      newProcessTime: "",
      newProcessDetail: "",
      newProcessPosition: 0,
      note: "",
      resultText: "",
      whatIsIt: "",
      howDoesItWork: "",
      howIsItUsed: "",
      howWellDoesItWork: "",
      whatAreTheSideEffect: "",
      considerations: [],
      newConsideration: "",
      newConsiderationPosition: 0,
      newFaqQuestion: "",
      newFaqAnswer: "",
      newFaqPosition: 0,
      faqs: [],
      imageUrl: "",
      imageFile: "",
      imagePicker: [],
      position: 0,
      color: "#000000",
      removeFromDetailsPage: false,
      applicableCountries: ["South Africa"],
      methodTypes: [
        { name: "Pills", displayName: "Pills" },
        { name: "Rings", displayName: "Rings" },
        { name: "Injections", displayName: "Injections" },
      ],
      selectedMethodType: "Pills",
      countries: ["South Africa", "Kenya", "Uganda", "Tanzania"],
      allQuestions: [],
      negatingQuestions: [],
    };
  },
  async created() {
    this.allMethods = await MethodDataService.getAll();
    this.allQuestions = await QuestionDataService.getAll();
  },
  computed: {
    sortedProcess() {
      return [...this.process].sort(
        (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0),
      );
    },
  },
  methods: {
    toggleValue(list, value) {
      const index = list.indexOf(value);
      if (index > -1) {
        list.splice(index, 1);
      } else {
        list.push(value);
      }
    },
    async validate() {
      const { valid } = await this.$refs.form.validate();
      if (valid) alert("Form is valid");
    },
    reset() {
      this.editing = false;
      this.editingItem = "";
      this.name = "";
      this.selectedMethodType = "Pills";
      this.tagline = "";
      this.schedule = "";
      this.process = [];
      this.note = "";
      this.resultText = "";
      this.whatIsIt = "";
      this.howDoesItWork = "";
      this.howIsItUsed = "";
      this.howWellDoesItWork = "";
      this.whatAreTheSideEffect = "";
      this.considerations = [];
      this.faqs = [];
      this.imageUrl = "";
      this.imageFile = "";
      this.imagePicker = [];
      this.position = 0;
      this.color = "#000000";
      this.removeFromDetailsPage = false;
      this.applicableCountries = ["South Africa"];
      this.negatingQuestions = [];
    },
    addProcessStep() {
      if (!this.newProcessTime || !this.newProcessDetail) return;
      this.process.push({
        time: this.newProcessTime,
        detail: this.newProcessDetail,
        position: this.newProcessPosition,
      });
      this.newProcessTime = "";
      this.newProcessDetail = "";
      this.newProcessPosition = 0;
    },
    deleteProcessStep(step) {
      const index = this.process.indexOf(step);
      if (index > -1) {
        this.process.splice(index, 1);
      }
    },
    addConsideration() {
      console.log(this.newConsideration);
      this.considerations.push({
        text: this.newConsideration,
        position: this.newConsiderationPosition,
      });
      this.newConsideration = "";
      this.newConsiderationPosition = 0;
    },
    deleteConsideration(index) {
      this.considerations.splice(index, 1);
    },
    addFaq() {
      this.faqs.push({
        question: this.newFaqQuestion,
        answer: this.newFaqAnswer,
        position: this.newFaqPosition,
      });
      this.newFaqQuestion = "";
      this.newFaqAnswer = "";
      this.newFaqPosition = 0;
    },
    deleteFaq(index) {
      this.faqs.splice(index, 1);
    },
    async onFilePicked(e) {
      if (!e.target.files.length) return;
      const files = e.target.files;
      if (files[0] !== undefined) {
        this.imageFile = files[0];
        console.log(this.imageFile);
        this.imageUrl = await MethodDataService.uploadImage(this.imageFile);
        console.log(this.imageUrl);
      } else {
        this.imageFile = "";
      }
    },
    async save() {
      const { valid } = await this.$refs.form.validate();
      if (valid) {
        if (this.color.endsWith("NAN")) {
          this.color = this.color.substring(0, this.color.length - 3);
        }
        const method = {
          name: this.name,
          methodType: this.selectedMethodType,
          tagline: this.tagline,
          schedule: this.schedule,
          process: this.process,
          note: this.note,
          resultText: this.resultText,
          whatIsIt: this.whatIsIt,
          howDoesItWork: this.howDoesItWork,
          howIsItUsed: this.howIsItUsed,
          howWellDoesItWork: this.howWellDoesItWork,
          whatAreTheSideEffect: this.whatAreTheSideEffect,
          considerations: this.considerations,
          faqs: this.faqs,
          imageUrl: this.imageUrl,
          position: this.position,
          color: this.color,
          removeFromDetailsPage: this.removeFromDetailsPage,
          applicableCountries: this.applicableCountries,
          negatingQuestions: this.negatingQuestions,
        };
        console.log(method);
        if (this.editing && this.editingItem && this.editingItem.id) {
          var updateResponse = await MethodDataService.update(
            this.editingItem.id,
            method,
          );
          if (updateResponse) {
            this.$swal("Method updated successfully");
          } else {
            this.$swal("Error updating method", "", "error");
          }
        } else {
          var createResponse = await MethodDataService.create(method);
          console.log("createResponse:", createResponse);
          if (createResponse) {
            this.$swal("Method created successfully");
          }
        }
        this.reset();
        this.allMethods = await MethodDataService.getAll();
      }
    },
    editItem(item) {
      this.editing = true;
      this.editingItem = item;
      this.name = item.name || "";
      this.selectedMethodType = item.methodType || "Pills";
      this.tagline = item.tagline || "";
      this.schedule = item.schedule || "";
      this.process = item.process || [];
      this.note = item.note || "";
      this.resultText = item.resultText || "";
      this.whatIsIt = item.whatIsIt || "";
      this.howDoesItWork = item.howDoesItWork || "";
      this.howIsItUsed = item.howIsItUsed || "";
      this.howWellDoesItWork = item.howWellDoesItWork || "";
      this.whatAreTheSideEffect = item.whatAreTheSideEffect || "";
      this.considerations = item.considerations || [];
      this.faqs = item.faqs || [];
      this.imageUrl = item.imageUrl || "";
      this.position = item.position || 0;
      this.color = item.color || "#000000";
      this.removeFromDetailsPage = item.removeFromDetailsPage || false;
      this.applicableCountries = item.applicableCountries || [];
      this.negatingQuestions = item.negatingQuestions || [];
    },
    async deleteItem(item) {
      console.log(item);
      this.$swal({
        title: "Are you sure you want to delete this item?",
        showCancelButton: true,
        confirmButtonText: "Yes",
      }).then(async (result) => {
        if (result.isConfirmed) {
          var deleteResponse = await MethodDataService.delete(item.id);
          if (deleteResponse) {
            this.$swal("Method deleted successfully");
            if (this.editingItem && this.editingItem.id === item.id) {
              this.reset();
            }
            this.allMethods = await MethodDataService.getAll();
          } else {
            this.$swal("Error deleting method", "", "error");
          }
        } else if (result.isDismissed) {
          this.$swal("Cancelled", "", "info");
        }
      });
    },
    async cloneItem(item) {
      const clonedMethod = {
        name: `${item.name} - Copy`,
        methodType: item.methodType || "Pills",
        tagline: item.tagline || "",
        schedule: item.schedule || "",
        process: item.process ? [...item.process] : [],
        note: item.note || "",
        resultText: item.resultText || "",
        whatIsIt: item.whatIsIt || "",
        howDoesItWork: item.howDoesItWork || "",
        howIsItUsed: item.howIsItUsed || "",
        howWellDoesItWork: item.howWellDoesItWork || "",
        whatAreTheSideEffect: item.whatAreTheSideEffect || "",
        considerations: item.considerations ? [...item.considerations] : [],
        faqs: item.faqs ? [...item.faqs] : [],
        imageUrl: item.imageUrl || "",
        position: (item.position || 0) + 1, // Increment position to avoid conflicts
        color: item.color || "#000000",
        removeFromDetailsPage: item.removeFromDetailsPage || false,
        applicableCountries: item.applicableCountries
          ? [...item.applicableCountries]
          : ["South Africa"],
        negatingQuestions: item.negatingQuestions
          ? [...item.negatingQuestions]
          : [],
      };

      console.log("Cloning method:", clonedMethod);
      const createResponse = await MethodDataService.create(clonedMethod);
      if (createResponse) {
        this.$swal("Method cloned successfully");
        this.allMethods = await MethodDataService.getAll();
      } else {
        this.$swal("Error cloning method", "", "error");
      }
    },
    cleanNegatingQuestions() {
      this.allMethods.forEach(async (method) => {
        const { id, negatingQuestions } = method;
        if (!negatingQuestions || negatingQuestions.length === 0) return;

        const cleanedNegatingQuestions = negatingQuestions.filter(
          (questionId) =>
            this.allQuestions.some((question) => question.id === questionId),
        );

        if (cleanedNegatingQuestions.length === negatingQuestions.length) {
          return; // Nothing stale to remove
        }

        const updateResponse = await MethodDataService.update(id, {
          negatingQuestions: cleanedNegatingQuestions,
        });
        if (updateResponse) {
          console.log("Method updated successfully:", id);
        } else {
          console.log("Error updating method:", id);
        }
      });
      this.$swal("Stale negating questions cleaned up");
    },
  },
};
</script>

<style scoped>
.admin-page {
  padding-bottom: 24px;
}

.admin-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.94) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1) !important;
}

.admin-card-title {
  display: flex;
  align-items: center;
  font-family: "Montserrat", sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: #3d80e8;
  padding: 16px 20px;
}

.admin-field :deep(.v-field) {
  border-radius: 12px;
}

.admin-field :deep(.v-field--focused .v-field__outline) {
  color: #3d80e8;
}

.admin-color-picker {
  border-radius: 12px;
  overflow: hidden;
}

.admin-section-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a92a3;
  margin: 20px 0 10px;
}

.admin-section-label:first-child {
  margin-top: 0;
}

.admin-section-label-hint {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  color: #a4abb8;
}

.admin-section-label-error {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
  color: #d32f2f;
}

.method-chip-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.method-chip {
  border: 2px solid rgba(61, 128, 232, 0.35);
  background: #ffffff;
  color: #3d80e8;
  border-radius: 99px;
  padding: 8px 16px;
  font-weight: 700;
  font-size: 0.82rem;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.method-chip.active {
  background: #3d80e8;
  border-color: #3d80e8;
  color: #ffffff;
}

.method-chip:hover:not(.active) {
  background: rgba(61, 128, 232, 0.08);
}

.method-chip-small {
  padding: 5px 12px;
  font-size: 0.76rem;
}

.pill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 99px;
  padding: 10px 22px;
  font-weight: 700;
  font-size: 0.85rem;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}

.pill-btn-outline {
  background: transparent;
  border: 2px solid #3d80e8;
  color: #3d80e8;
}

.pill-btn-outline:hover {
  background: rgba(61, 128, 232, 0.08);
}

.pill-btn-danger {
  border-color: #eb63eb;
  color: #eb63eb;
}

.pill-btn-danger:hover {
  background: rgba(235, 99, 235, 0.08);
}

.pill-btn-solid {
  background: #3d80e8;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(61, 128, 232, 0.3);
}

.pill-btn-solid:hover {
  background: #326fd1;
}

.pill-btn-add {
  border: 2px dashed rgba(61, 128, 232, 0.4);
  color: #3d80e8;
  background: transparent;
  padding: 8px 18px;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.pill-btn-add:hover {
  background: rgba(61, 128, 232, 0.08);
}

.admin-subsection {
  background: #f4f6fa;
  border: 1px solid #e3e7ef;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
}

.admin-subsection-row {
  display: flex;
  align-items: center;
}

.admin-subsection-label {
  font-weight: 700;
  font-size: 0.9rem;
  color: #2c3444;
  margin-bottom: 10px;
}

.admin-option-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
}

.option-card {
  background: #ffffff;
  border: 1px solid #e3e7ef;
  border-left: 3px solid #3d80e8;
  border-radius: 10px;
  padding: 10px 14px;
}

.option-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.option-card-label {
  font-weight: 600;
  color: #2c3444;
  font-size: 0.85rem;
}

.option-card-answer {
  color: #6b7280;
  font-size: 0.82rem;
  margin: 8px 0 0;
}

.admin-option-delete {
  cursor: pointer;
  color: #9aa1ae;
  flex-shrink: 0;
}

.admin-option-delete:hover {
  color: #d32f2f;
}

.admin-option-empty {
  color: #9aa1ae;
  font-size: 0.82rem;
  font-style: italic;
  margin: 0;
}

.admin-image-preview {
  border-radius: 8px;
  flex-shrink: 0;
}

.admin-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.method-tab-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  padding: 8px 4px 16px;
}

.method-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  padding: 4px 4px 10px;
  transition: border-color 0.2s ease;
}

.method-tab-icon-wrap {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.method-tab-icon {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.method-tab-label {
  color: #3d80e8;
  font-weight: 700;
  font-size: 0.82rem;
  font-family: "Montserrat", sans-serif;
  max-width: 90px;
  text-align: center;
  line-height: 1.2;
}

.method-tab.active {
  border-bottom-color: #eba614;
}

.method-tab.active .method-tab-label {
  color: #eba614;
}

.method-tab-selected-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(61, 128, 232, 0.06);
  border-radius: 10px;
  margin-bottom: 8px;
}

.method-tab-selected-label {
  font-weight: 700;
  color: #2c3444;
  font-size: 0.85rem;
}

.pill-btn-small {
  padding: 6px 14px;
  font-size: 0.75rem;
}
</style>
